import type { ParseError } from '../result.ts';
import type { ImageRef } from './media.ts';
import type { RichNode } from './richText.ts';
import type { VideoSummary } from './video.ts';

/**
 * §6 — `indexId` is present **only** on Invidious-owned playlists; a YouTube playlist omits the
 * field entirely (not `null` — absent). Typing it non-nullable would empty every YouTube playlist
 * under R4's drop-malformed-items rule, which is why the corpus commits one of each shape.
 *
 * The two numbers are not interchangeable, and the API's error message hides that: removal keys on
 * `indexId`, and `DELETE …/videos/{index}` with the numeric position answers
 * `404 "Playlist does not contain index"` — which reads as "no such position" even when the
 * playlist plainly has one. Verified on the wire in 0g.
 */
export interface PlaylistEntry {
  readonly indexId: string | null;
  /** 0-based position. Display and ordering only — never a removal key. */
  readonly index: number;
  readonly video: VideoSummary;
}

/** The listing form: a search result, or a playlist rail. Carries no entries. */
export interface PlaylistSummary {
  readonly playlistId: string;
  readonly title: string;
  readonly author: string;
  readonly authorId: string | null;
  /** Relative (`/channel/UC…`) — an in-app route, not a URL to fetch. */
  readonly authorUrl: string | null;
  readonly authorVerified: boolean;
  readonly videoCount: number | null;
  /** From `playlistThumbnail` (a bare `i.ytimg.com` URL), rewritten to the instance (R2). */
  readonly thumbnail: ImageRef | null;
  /** {@link isInvidiousPlaylistId} — decides whether this playlist is editable through the API. */
  readonly invidiousOwned: boolean;
}

/** `GET /api/v1/playlists/{id}`. */
export interface Playlist {
  readonly playlistId: string;
  readonly title: string;
  readonly author: string;
  readonly authorId: string | null;
  readonly authorUrl: string | null;
  readonly authorThumbnails: readonly ImageRef[];
  readonly description: string;
  /**
   * R8's node tree. Built from the plain `description`, which is the only field that reliably has
   * the text at all — a captured YouTube playlist answers `descriptionHtml: "<p></p>"` while its
   * `description` carries the whole thing.
   */
  readonly descriptionNodes: readonly RichNode[];
  /** What the API reports. Not necessarily `entries.length`: the response is paged. */
  readonly videoCount: number | null;
  readonly viewCount: number | null;
  readonly updatedEpochSec: number | null;
  readonly isListed: boolean;
  readonly thumbnail: ImageRef | null;
  readonly invidiousOwned: boolean;
  readonly entries: readonly PlaylistEntry[];
  /**
   * R4 — a malformed entry drops itself *and is counted*. Without this the count dies with the
   * `Result` and "13 of 14 videos rendered" becomes indistinguishable from "the playlist has 13".
   */
  readonly dropped: readonly ParseError[];
}

/**
 * Invidious mints its own playlist IDs as `IVPL` + 32 random characters; YouTube's are `PL…`,
 * `UU…`, `LL…`, `RD…` and friends. This prefix is the only thing that says whether the mutation
 * endpoints (`PATCH`/`DELETE`/add-video) apply — an `IVPL…` id is ours to edit, anything else is a
 * read-only mirror of YouTube.
 *
 * Per-*entry*, the operative fact is still `indexId`: an Invidious playlist fetched before its
 * entries were written would report the prefix without a removal key.
 */
export function isInvidiousPlaylistId(playlistId: string): boolean {
  return playlistId.startsWith('IVPL');
}
